import {
    BlendState,
    BUFFER_STATIC,
    Color,
    CULLFACE_NONE,
    Entity,
    IndexBuffer,
    INDEXFORMAT_UINT16,
    Mesh,
    MeshInstance,
    PRIMITIVE_TRIANGLES,
    SEMANTIC_POSITION,
    StandardMaterial,
    TYPE_FLOAT32,
    TYPE_UINT16,
    Vec3,
    VertexBuffer,
    VertexFormat
} from 'playcanvas';

import { Element, ElementType } from './element';
import { Pivot } from './pivot';

// A tiny 3D marker that shows the current orbit pivot.
// It fades out after a short time so it stays informative but unobtrusive.
class PivotMarker extends Element {
    private _entity: Entity;
    private _material: StandardMaterial;
    private _ttl = 0;
    private _duration = 2.0;

    private _built = false;

    private _onPivotPlaced?: (pivot: Pivot) => void;
    private _onPivotChanged?: (_element: unknown) => void;

    constructor() {
        super(ElementType.debug);

        this._entity = new Entity('pivotMarker');

        // We use a render component with 3 meshInstances so we can draw
        // three perpendicular rings (XY, XZ, YZ) like a simple orbit gizmo.
        this._entity.addComponent('render');

        const mat = new StandardMaterial();
        mat.name = 'pivotMarkerMat';
        mat.useLighting = false;
        mat.emissive = new Color(0.3, 0.8, 1.0);
        mat.emissiveIntensity = 3.0;
        // Non-transparent
        mat.opacity = 1.0;
        mat.blendState = new BlendState(false);
        mat.cull = CULLFACE_NONE;
        // The marker should be readable even when the picked point is on/inside geometry.
        // Disable depth test/write so it always shows on top.
        mat.depthTest = false;
        mat.depthWrite = false;
        mat.update();

        this._material = mat;

        // NOTE: we build the ring meshes in add() because PivotMarker.scene is assigned
        // after construction in Scene constructor.
    }

    add() {
        if (!this._built) {
            this._built = true;

            const device = this.scene?.graphicsDevice;

            // Guaranteed ring mesh generator (no device.createTorus dependency)
            // Produces a thin triangle strip ring in the XY plane.
            const createRingMesh = (radius = 0.5, thickness = 0.03, segments = 96) => {
                if (!device) return null;

                const mesh = new Mesh(device);

                const vertexFormat = new VertexFormat(device, [{
                    semantic: SEMANTIC_POSITION,
                    components: 3,
                    type: TYPE_FLOAT32
                }]);

                const vertCount = (segments + 1) * 2;
                const positions = new Float32Array(vertCount * 3);

                const inner = Math.max(0.0001, radius - thickness * 0.5);
                const outer = radius + thickness * 0.5;

                for (let i = 0; i <= segments; i++) {
                    const a = (i / segments) * Math.PI * 2;
                    const ca = Math.cos(a);
                    const sa = Math.sin(a);

                    // inner
                    let o = (i * 2 + 0) * 3;
                    positions[o + 0] = ca * inner;
                    positions[o + 1] = sa * inner;
                    positions[o + 2] = 0;

                    // outer
                    o = (i * 2 + 1) * 3;
                    positions[o + 0] = ca * outer;
                    positions[o + 1] = sa * outer;
                    positions[o + 2] = 0;
                }

                const indexCount = segments * 6;
                const indices = new Uint16Array(indexCount);
                let ii = 0;
                for (let i = 0; i < segments; i++) {
                    const i0 = i * 2;
                    const i1 = i * 2 + 1;
                    const i2 = (i + 1) * 2;
                    const i3 = (i + 1) * 2 + 1;
                    // two triangles
                    indices[ii++] = i0;
                    indices[ii++] = i2;
                    indices[ii++] = i1;
                    indices[ii++] = i1;
                    indices[ii++] = i2;
                    indices[ii++] = i3;
                }

                const vb = new VertexBuffer(device, vertexFormat, vertCount, {
                    usage: BUFFER_STATIC,
                    data: positions.buffer
                });
                // IndexBuffer signature in this PlayCanvas build:
                // new IndexBuffer(device, format, numIndices, usage?, initialData?)
                // IMPORTANT: IndexBuffer expects INDEXFORMAT_* (not TYPE_*). Passing TYPE_UINT16
                // causes invalid GL enums / NaN sizing.
                // IndexBuffer.setData expects an ArrayBuffer of the exact expected size.
                const ib = new IndexBuffer(device, INDEXFORMAT_UINT16, indexCount, BUFFER_STATIC, indices.buffer);

                mesh.vertexBuffer = vb;
                mesh.indexBuffer[0] = ib;
                mesh.primitive[0].type = PRIMITIVE_TRIANGLES;
                mesh.primitive[0].base = 0;
                mesh.primitive[0].count = indexCount;
                mesh.primitive[0].indexed = true;

                mesh.aabb.compute(positions);
                return mesh;
            };

            const ringMesh = createRingMesh(0.5, 0.04, 128);
            if (ringMesh) {
                const miXY = new MeshInstance(ringMesh, this._material);
                const miXZ = new MeshInstance(ringMesh, this._material);
                const miYZ = new MeshInstance(ringMesh, this._material);

                this._entity.render.meshInstances = [miXY];

                const xz = new Entity('pivotMarkerRingXZ');
                xz.addComponent('render');
                xz.render.meshInstances = [miXZ];
                xz.setLocalEulerAngles(90, 0, 0);
                this._entity.addChild(xz);

                const yz = new Entity('pivotMarkerRingYZ');
                yz.addComponent('render');
                yz.render.meshInstances = [miYZ];
                yz.setLocalEulerAngles(0, 90, 0);
                this._entity.addChild(yz);
            }
        }

        // Render on overlay layer so it remains visible even when gizmoLayer is disabled
        // (e.g. during certain render/export modes). This is purely a visual indicator.
        this._entity.render.layers = [this.scene.overlayLayer.id];

        // Ensure child ring entities (if any) also render on the same layer.
        for (const child of this._entity.children) {
            if ((child as any).render) {
                (child as any).render.layers = [this.scene.overlayLayer.id];
            }
        }
        this.scene.contentRoot.addChild(this._entity);
        this._entity.enabled = false;

        // Show the marker at the current transform pivot whenever it is placed/changed.
        // This implicitly reflects element pivot offsets because the pivot placement logic
        // in the active transform handler includes pivotOffset.
        if (!this._onPivotPlaced) {
            this._onPivotPlaced = (pivot: Pivot) => {
                try {
                    const p = pivot?.transform?.position;
                    if (!p) return;
                    const radius = this.scene?.bound?.halfExtents?.length?.() ?? 1;
                    this.show(p, radius, 0.75);
                } catch (e) {
                    // best effort
                }
            };
            this.scene.events.on('pivot.placed', this._onPivotPlaced);
        }

        if (!this._onPivotChanged) {
            this._onPivotChanged = () => {
                // When pivot offsets change, the active handler should re-place the pivot,
                // which will trigger pivot.placed. This listener exists as a safety net
                // for cases where other code sets pivotOffset without placing the pivot.
                try {
                    const pivot = this.scene.events.invoke('pivot') as Pivot;
                    if (!pivot) return;
                    const radius = this.scene?.bound?.halfExtents?.length?.() ?? 1;
                    this.show(pivot.transform.position, radius, 0.75);
                } catch (e) {
                    // ignore
                }
            };
            this.scene.events.on('pivot.changed', this._onPivotChanged);
        }
    }

    remove() {
        if (this._onPivotPlaced) {
            this.scene?.events.off('pivot.placed', this._onPivotPlaced);
            this._onPivotPlaced = undefined;
        }
        if (this._onPivotChanged) {
            this.scene?.events.off('pivot.changed', this._onPivotChanged);
            this._onPivotChanged = undefined;
        }
        this.scene.contentRoot.removeChild(this._entity);
    }

    show(position: Vec3, radius: number, durationSeconds = 2.0) {
        this._duration = Math.max(0.1, durationSeconds);
        this._ttl = this._duration;
        this._entity.enabled = true;
        this._entity.setPosition(position);

        // make marker roughly scale with scene radius but clamp to a sensible range,
        // and make it HALF the previous size.
        const r = Math.max(0.03, Math.min(0.6, radius * 0.02)) * 0.25;
        this._entity.setLocalScale(r, r, r);
    }

    onUpdate(dt: number) {
        if (!this._entity.enabled) return;
        this._ttl -= dt;
        const t = Math.max(0, Math.min(1, this._ttl / this._duration));

        if (this._ttl <= 0) {
            this._entity.enabled = false;
        }
    }
}

export { PivotMarker };
